import { useCallback, useId, useMemo, useRef, useState } from "react";
import { BookOpen, LogOut, ReceiptText, Shield, User as UserIcon } from "lucide-react";
import { Link, useNavigate } from "react-router";
import type { CurrentUser } from "@jipraks/shared";
import { UserAvatar } from "@client/components/ui/UserAvatar";
import { useAuth } from "@client/hooks/use-auth";
import { useDismiss } from "@client/hooks/use-dismiss";
import { authCopy } from "@client/locales/id/auth";

const copy = authCopy.menu;

const ITEM =
  "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm text-foreground-muted transition-colors duration-200 ease-expo-out hover:bg-surface hover:text-foreground focus-visible:bg-surface focus-visible:text-foreground";

export function UserMenu({ user }: { user: CurrentUser }) {
  const { viewMode, setViewMode, signOut } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = useCallback(() => setIsOpen(false), []);
  const dismissRefs = useMemo(() => [containerRef], []);
  useDismiss(dismissRefs, isOpen, close);

  function switchMode() {
    const next = viewMode === "admin" ? "user" : "admin";
    setViewMode(next);
    close();
    void navigate(next === "admin" ? "/admin" : "/");
  }

  async function handleSignOut() {
    close();
    await signOut();
    void navigate("/");
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-label={copy.openMenu}
        className="flex min-h-11 items-center gap-2 rounded-full p-1.5 transition-colors duration-200 ease-expo-out hover:bg-surface"
      >
        <UserAvatar name={user.fullName} email={user.email} avatarUrl={user.avatarUrl} />
      </button>

      {isOpen && (
        <div
          id={menuId}
          className="animate-fade-up absolute right-0 z-50 mt-2 w-[min(18rem,calc(100vw-2rem))] rounded-2xl border border-border-default bg-background-elevated/95 p-2 shadow-card backdrop-blur-xl [animation-duration:200ms]"
        >
          <div className="border-b border-border-default px-3 pt-2 pb-3">
            <p className="truncate text-sm font-medium text-foreground">{user.fullName ?? user.email}</p>
            <p className="truncate text-xs text-foreground-muted">{user.email}</p>
          </div>
          <nav className="flex flex-col gap-0.5 pt-2">
            <Link to="/library" className={ITEM} onClick={close}>
              <BookOpen className="size-4" aria-hidden="true" />
              {copy.library}
            </Link>
            <Link to="/invoices" className={ITEM} onClick={close}>
              <ReceiptText className="size-4" aria-hidden="true" />
              {copy.invoices}
            </Link>
            {user.isAdmin && (
              <button type="button" className={ITEM} onClick={switchMode}>
                {viewMode === "admin" ? (
                  <UserIcon className="size-4" aria-hidden="true" />
                ) : (
                  <Shield className="size-4" aria-hidden="true" />
                )}
                {viewMode === "admin" ? copy.switchToUser : copy.switchToAdmin}
              </button>
            )}
            <button type="button" className={ITEM} onClick={() => void handleSignOut()}>
              <LogOut className="size-4" aria-hidden="true" />
              {copy.signOut}
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
